import * as React from "react";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "destructive" | "link" | "tab" | "close" | "brand" | "card" | "icon" | "default";
  /** Optional icon to render alongside the button text */
  icon?: React.ReactNode;
  /** Where the icon should appear relative to the children. Defaults to left. */
  iconPosition?: "left" | "right";
  size?: "default" | "micro" | "icon";
  /** For tab variant: whether this tab is active */
  active?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      variant = "default",
      size = "default",
      icon,
      iconPosition = "left",
      active,
      children,
      ...props
    },
    ref
  ) => {
    const base =
      "inline-flex items-center justify-center text-sm font-medium transition-all focus-visible:outline-none disabled:opacity-50 disabled:pointer-events-none";

    const styles =
      variant === "primary"
        ? "bg-neutral-900 text-white hover:bg-neutral-800 rounded-xl"
        : variant === "secondary"
          ? "bg-white border border-neutral-200 text-neutral-900 hover:bg-neutral-50 rounded-xl"
          : variant === "ghost"
            ? "bg-white border border-neutral-200 text-neutral-900 hover:bg-neutral-100 rounded-xl"
            : variant === "destructive"
              ? "bg-white border border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
              : variant === "link"
                ? "text-neutral-600 hover:text-neutral-900 underline-offset-4 hover:underline"
                : variant === "tab"
                  ? active
                    ? "bg-white text-neutral-900 shadow-sm rounded-lg"
                    : "text-neutral-600 hover:text-neutral-900 rounded-lg"
                  : variant === "close"
                    ? "text-neutral-400 hover:text-neutral-600 transition-colors"
                    : variant === "brand"
                      ? "bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] hover:from-[#0DAFFD] hover:to-[#0FD8FE] text-white font-semibold rounded-xl"
                      : variant === "card"
                        ? "bg-white border border-neutral-200 text-neutral-900 hover:bg-neutral-100 flex-shrink-0 rounded-xl"
                        : variant === "icon"
                          ? "text-neutral-500 hover:text-neutral-700"
                          : "bg-neutral-900 text-white hover:bg-neutral-800 rounded-md";

    const sizes =
      variant === "icon" || variant === "close"
        ? "p-1"
        : variant === "link"
          ? "p-0"
          : variant === "tab"
            ? "flex-1 px-4 py-2.5"
            : size === "micro"
              ? "min-h-6 px-2 text-xs"
              : size === "icon"
                ? "h-9 w-9 p-0"
                : "min-h-10 px-4 py-2";

    const hasChildren = React.Children.count(children) > 0;

    return (
      <button ref={ref} className={`${base} ${styles} ${sizes} ${className}`} {...props}>
        {icon && iconPosition === "left" && (
          <span className={hasChildren ? "mr-2" : undefined}>{icon}</span>
        )}
        {children}
        {icon && iconPosition === "right" && (
          <span className={hasChildren ? "ml-2" : undefined}>{icon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
